import { IconMail } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getApiErrorDetails, getApiErrorMessage } from "@/lib/api-error";
import { cn } from "@/lib/utils";

interface PasswordLoginFormProps {
  onLogin: (email: string, password: string) => Promise<void>;
  onForgotPassword: (prefillEmail: string) => void;
  className?: string;
}

/**
 * Email + password login (ADP-167, replaces the OTP form for login mode).
 * A passwordless legacy/OAuth account gets routed into the forgot-password
 * flow via the `passwordNotSet` hint on the 401 response.
 */
export function PasswordLoginForm({
  onLogin,
  onForgotPassword,
  className,
}: PasswordLoginFormProps) {
  const { t } = useTranslation("auth");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordNotSet, setPasswordNotSet] = useState(false);

  async function handleSubmit() {
    const submittedEmail = email.trim().toLowerCase();
    if (!submittedEmail.includes("@")) {
      setError(t("login.invalidEmail"));
      return;
    }
    if (!password) {
      setError(t("login.passwordRequired"));
      return;
    }
    setError(null);
    setPasswordNotSet(false);
    setLoading(true);
    try {
      await onLogin(submittedEmail, password);
    } catch (err) {
      const details = await getApiErrorDetails(err);
      if (details && typeof details === "object" && "passwordNotSet" in details) {
        setPasswordNotSet(true);
        setError(t("login.passwordNotSet"));
      } else {
        setError(await getApiErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <form
      className={cn("flex min-h-[320px] flex-col gap-7", className)}
      onSubmit={(event) => {
        event.preventDefault();
        void handleSubmit();
      }}
    >
      <div>
        <div className="mb-4 flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <IconMail className="size-5" />
        </div>
        <h2 className="text-2xl font-semibold tracking-tight text-foreground">
          {t("login.welcomeTitle")}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">{t("login.welcomeDescription")}</p>
      </div>

      <div className="flex flex-col gap-3">
        <Input
          id="email"
          type="email"
          aria-label={t("login.emailPlaceholder")}
          placeholder={t("login.emailPlaceholder")}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="email"
          required
          size="lg"
        />
        <Input
          id="password"
          type="password"
          aria-label={t("login.passwordPlaceholder")}
          placeholder={t("login.passwordPlaceholder")}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          required
          size="lg"
        />
        {error ? <p className="text-sm text-destructive-foreground">{error}</p> : null}
        <Button type="submit" size="lg" disabled={loading} className="w-full">
          {loading ? t("login.signingIn") : t("login.signIn")}
        </Button>
        <button
          type="button"
          className="self-end text-sm text-primary hover:underline"
          onClick={() => onForgotPassword(email.trim().toLowerCase())}
        >
          {passwordNotSet ? t("login.forgot.title") : t("login.forgotPassword")}
        </button>
      </div>
    </form>
  );
}
